import { Component, Input, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { DialogService } from '@progress/kendo-angular-dialog';
import { Subscription } from 'rxjs';
import { formComponentMapOptions } from '~/front-models/form-component-map';
import { LazyLoaderService } from '~/helpers/lazy-loader';
import { SharedService } from '~/services/sharedService';
import { LayoutManagerService } from '../layout/layout-manager';

@Component({
    selector: 'asap-dialog-layout',
    templateUrl: './dialog-layout.component.html',
    styleUrls: ['./dialog-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class DialogLayoutComponent implements OnInit {
  @Input() componentName: string;
  @Input() data: any;
  @Output() closeDialog = new EventEmitter<any>();
  closeDialogSubscription: Subscription;
  dialogResultSubscription: Subscription;

  private dialog: any;
  private _isDestroyed = false;

  constructor(
    private _dialogService: DialogService,
    private _sharedService: SharedService,
    private _lazyLoaderService: LazyLoaderService,
    private _layoutManager: LayoutManagerService
  ) {}

  async ngOnInit(): Promise<void> {
    this._sharedService.model = this.data;
    if (this._lazyLoaderService && this.componentName) {
      await this._lazyLoaderService.load(this.componentName);
    }
   
    if (this._isDestroyed) {
      return;
    }

    const componentContent = formComponentMapOptions[this.componentName];
    if (!componentContent) {
      console.error(`Component not found for name: ${this.componentName}`);
      return;
    }

    this.dialog = this._dialogService.open({ content: componentContent });

    this.dialogResultSubscription = this.dialog.result?.subscribe(() => {
      this._layoutManager.removeOverlay();
      this.closeDialog.emit(false);
    });

    this.closeDialogSubscription = this._sharedService.closeDialog.subscribe((result: any) => {
      this._sharedService.formDeactivated = true;

      if (result && result.isClosed) {
        this._onCloseDialog(result.component, result.response);
      }
    });
  }

  _onCloseDialog(component: string, response: any = null) {
    const isMatchingComponent =
      !component ||
      this.componentName == component ||
      (this.componentName?.toLowerCase().includes('report') && component?.toLowerCase().includes('report')) ||
      (this.componentName?.toLowerCase().includes('forecast') && component?.toLowerCase().includes('forecast'));

    if (this.dialog && isMatchingComponent) {
      if (this.dialog) {
        try {
          this.dialog.close();
        } catch (e) {
          console.warn('Error closing dialog:', e);
        }
        this.dialog = null;
      }

      this._layoutManager.removeOverlay();

      if (!response) {
        var responseModel: any = {
          open: false,
          response: response,
          component: component
        };

        this.closeDialog.emit(responseModel);
      }
      else {
        this.closeDialog.emit(false);
      }
    }
  }

  _onCloseFilesDialog(component: string, response: any = null, picObj: any) {
    if (this.dialog && this.componentName == component) {
      if (this.dialog) {
        try {
          this.dialog.close();
        } catch (e) {
          console.warn('Error closing dialog:', e);
        }
        this.dialog = null;
      }

      this._layoutManager.removeOverlay();

      if (response != null) {
        var responseModel: any = {
          open: false,
          response: response,
          component: component
        };

        this.closeDialog.emit(picObj);
      }
      else {
        this.closeDialog.emit(picObj);
      }
    }
  }

  ngOnDestroy() {
    this._isDestroyed = true;
    this._layoutManager.removeOverlay();

    if (this.closeDialogSubscription) {
      this.closeDialogSubscription.unsubscribe();
    }
    if (this.dialogResultSubscription) {
      this.dialogResultSubscription.unsubscribe();
    }
    if (this.dialog) {
      try {
        this.dialog.close();
      } catch (e) {
        console.warn('Error closing dialog on destroy:', e);
      }
      this.dialog = null;
    }
  }
}
